import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@bednbody/ui/lib/utils"

const badgeVariants = cva(
  "group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        success: "bg-success/15 text-success",
        accent: "bg-accent/15 text-accent",
        warning: "bg-warning/15 text-warning",
        info: "bg-info/15 text-info",
        primary: "bg-primary/15 text-primary",
        destructive:
          "bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20",
        muted: "bg-muted text-muted-foreground",
        outline:
          "border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost:
          "hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50",
        link: "text-primary underline-offset-4 hover:underline",
        gold: "text-gold-foreground bg-linear-to-br from-gold to-gold-light ring-gold-foreground/20",
        /**
         * Soft outline tones — a thin tinted border on a barely-there fill, for
         * status pills on dense operational lists (the admin live map). The
         * filled tones above shout; these sit quietly beside a name.
         */
        "primary-soft": "border-primary/25 bg-primary/5 text-primary-dark dark:text-primary-light",
        "success-soft": "border-success/30 bg-success/5 text-success",
        "info-soft": "border-info/30 bg-info/5 text-info",
        "warning-soft": "border-warning/35 bg-warning/5 text-warning",
        "destructive-soft": "border-destructive/30 bg-destructive/5 text-destructive",
        "gold-soft": "border-gold/40 bg-gold/10 text-gold-foreground",
        "muted-soft": "border-border bg-muted/40 text-muted-foreground",
      },
      /**
       * Tabular content — a countdown, a code, a slot time. The digits stop
       * jittering as they tick. `TherapistCard` used to reach in with a
       * per-instance `font-mono` className; a tone that a component needs is a
       * variant (frontend-standards §8).
       */
      mono: {
        true: "font-mono",
        false: "",
      },
      /**
       * A multi-line chip — the Scheduled "Available Slot" is label, date and
       * time range stacked. Releases the single-row height and the nowrap so
       * the extra lines are not clipped.
       */
      stacked: {
        true: "h-auto flex-col items-end gap-0 whitespace-normal",
        false: "",
      },
      /**
       * Corner shape. The default is the full pill again (2026-10-30, option
       * C; it was a 10px corner for a day); `soft` is the squarer
       * chip the admin console's tables use, matching Button's `soft` shape.
       */
      shape: {
        default: "",
        soft: "rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      mono: false,
      stacked: false,
      shape: "default",
    },
  }
)
export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>["variant"]>

function Badge({
  className,
  variant = "default",
  mono = false,
  stacked = false,
  shape = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant, mono, stacked, shape }), className),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
      mono,
      stacked,
      shape,
    },
  })
}

export { Badge, badgeVariants }
